import { EXO_SKELETON_EVENT_NAMES } from '../consts';
import { notInExoSkeletonError } from '../internal';
import {
  createPostMessageEventHandler,
  ExoSkeletonRuntimeDataEvent,
  isInExoSkeleton,
  sendRuntimeDataEvent,
} from '../runtime';

export interface ImportRemoteMessageData<
  METADATA extends Record<string, unknown>,
> {
  scope: string;
  metadata: METADATA;
}

type RuntimeImportRemoteEvent<METADATA extends Record<string, unknown>> =
  ExoSkeletonRuntimeDataEvent<
    typeof EXO_SKELETON_EVENT_NAMES.IMPORT_REMOTE_MESSAGE_EVENT_NAME,
    ImportRemoteMessageData<METADATA>
  >;

function isImportRemoteDataEvent<METADATA extends Record<string, unknown>>(
  data: unknown,
): data is RuntimeImportRemoteEvent<METADATA> {
  return (
    (data as RuntimeImportRemoteEvent<METADATA>)?.type ===
    EXO_SKELETON_EVENT_NAMES.IMPORT_REMOTE_MESSAGE_EVENT_NAME
  );
}

interface CreateImportRemoteListenerParams<T extends Record<string, unknown>> {
  onImportRemote: <METADATA extends T>(
    event: RuntimeImportRemoteEvent<METADATA>,
  ) => void;
}

const IMPORT_REMOTE_FN_LISTENER_NAME = 'createImportRemoteListener';

/**
 * Used to create a host for the ExoSkeleton.
 */
export function createImportRemoteListener<
  METADATA extends Record<string, unknown>,
>({ onImportRemote }: CreateImportRemoteListenerParams<METADATA>) {
  if (!isInExoSkeleton()) {
    throw notInExoSkeletonError({ source: IMPORT_REMOTE_FN_LISTENER_NAME });
  }
  return createPostMessageEventHandler(function ({
    data,
  }: MessageEvent<unknown>) {
    if (isImportRemoteDataEvent<METADATA>(data)) {
      onImportRemote(data);
    }
  });
}

export function sendImportRemoteRuntimeEvent<
  METADATA extends Record<string, unknown>,
>({ scope, metadata }: ImportRemoteMessageData<METADATA>) {
  return sendRuntimeDataEvent<
    typeof EXO_SKELETON_EVENT_NAMES.IMPORT_REMOTE_MESSAGE_EVENT_NAME,
    ImportRemoteMessageData<METADATA>
  >(EXO_SKELETON_EVENT_NAMES.IMPORT_REMOTE_MESSAGE_EVENT_NAME, {
    scope,
    metadata,
  });
}
